/* AtsDashboard — recruiter view + full-report drawer with auto-recommendation.
 *
 * Phase 3 additions:
 *  • "View full report" button per candidate opens a side drawer with:
 *      - Initial screening metrics (score, time, correct/wrong, per-question)
 *      - Pop-up answered/missed, time per question, paste, tab switches,
 *        refresh attempts, warnings.
 *      - Per-task reports (one card per task completed in the sandbox).
 *      - Auto Recommendation panel: Strong / Consider / Borderline / Reject
 *        with admin actions (Approve for manual contact / Reject politely /
 *        Keep for review / Close drawer).
 *  • No auto-action sent to candidate — admin decision required.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api/client";
import PopupsManager from "./PopupsManager";

function buildAdLink() {
  const payload = {
    s: "ad",
    t: Date.now().toString(36),
    r: Math.random().toString(36).slice(2, 10),
  };
  let b64 = "";
  try {
    b64 = btoa(JSON.stringify(payload))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch (_) { b64 = `vo${payload.t}${payload.r}`; }
  return `${window.location.origin}/r/${b64}`;
}

const STATUS_LABELS = {
  new: { ar: "جديد", color: "#94a3b8" },
  tested: { ar: "اختُبر", color: "#3b82f6" },
  strong: { ar: "قوي", color: "#16a34a" },
  trial: { ar: "تجريب", color: "#eab308" },
  reserve: { ar: "احتياط", color: "#a855f7" },
  rejected: { ar: "مرفوض", color: "#dc2626" },
};

const TIER_COLOR = {
  "Strong candidate": "#10b981",
  "Consider":         "#3b82f6",
  "Borderline":       "#eab308",
  "Reject":           "#dc2626",
};

export default function AtsDashboard() {
  const { user, logout } = useAuth() || {};
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [includeIncomplete, setIncludeIncomplete] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedAd, setCopiedAd] = useState(false);
  const [demoShare, setDemoShare] = useState(null);   // { pin, url, expires_at }
  const [demoBusy, setDemoBusy] = useState(false);
  const [drawerData, setDrawerData] = useState(null); // {applicant, recommendation}
  const [drawerLoading, setDrawerLoading] = useState(false);
  // Phase 3.6: selection + link toggle
  const [linkActive, setLinkActive] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const publicLink = `${window.location.origin}/assessment/visa-operations`;

  // Encrypted-looking ad link — different path (`/r/:tok`) and a base64
  // token. Bypasses any stale service-worker cache that might be bouncing
  // the legacy URL to /login. The token is just opaque tracking info
  // (source + timestamp) — no sensitive data.
  const [adLink] = useState(() => buildAdLink());

  const load = async () => {
    try {
      const { data: s } = await api.get("/ats/dashboard");
      setStats(s);
      const { data: list } = await api.get("/ats/applicants", {
        params: { status: statusFilter || undefined, q: q || undefined, include_incomplete: includeIncomplete || undefined },
      });
      setItems(list.items || []);
      setIncompleteCount(list.incomplete_count || 0);
      const { data: settings } = await api.get("/ats/settings");
      setLinkActive(!!settings.application_link_active);
    } catch (e) {
      console.error("ATS dashboard load failed", e);
    }
  };
  useEffect(() => { load(); }, [statusFilter, includeIncomplete]); // eslint-disable-line

  const setStatus = async (id, st) => {
    await api.patch(`/ats/applicants/${id}/status`, { status: st });
    load();
  };

  // ── Selection helpers
  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(items.map((it) => it.id)));
  const clearSelected = () => setSelected(new Set());
  const allSelected = items.length > 0 && selected.size === items.length;

  // ── Delete helpers
  const deleteOne = async (id, name) => {
    if (!window.confirm(`حذف نهائي للمرشح "${name}"؟ لا يمكن التراجع.`)) return;
    try {
      await api.delete(`/ats/applicants/${id}`);
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "تعذّر الحذف");
    }
  };
  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`حذف نهائي لـ ${selected.size} مرشّحاً؟ لا يمكن التراجع.`)) return;
    try {
      const { data } = await api.post("/ats/applicants/bulk-delete", { ids: Array.from(selected) });
      clearSelected();
      load();
      alert(`تم حذف ${data.deleted_count} من أصل ${data.requested}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "تعذّر الحذف الجماعي");
    }
  };

  // ── Link toggle
  const toggleLink = async () => {
    const nextActive = !linkActive;
    const verb = nextActive ? "تفعيل" : "إلغاء تفعيل";
    if (!window.confirm(`${verb} رابط التقديم العام؟`)) return;
    try {
      await api.patch("/ats/settings", { application_link_active: nextActive });
      setLinkActive(nextActive);
    } catch (e) {
      alert(e?.response?.data?.detail || "تعذّر التحديث");
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicLink); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (e) { /* ignore */ }
  };

  const copyAdLink = async () => {
    try { await navigator.clipboard.writeText(adLink); setCopiedAd(true); setTimeout(() => setCopiedAd(false), 1800); }
    catch (e) { /* ignore */ }
  };

  const generateDemoShare = async () => {
    setDemoBusy(true);
    try {
      const { data } = await api.post("/ats/demo-share");
      setDemoShare(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "تعذّر إنشاء رمز الديمو");
    } finally {
      setDemoBusy(false);
    }
  };

  const openCv = async (aid) => {
    // Open a tab synchronously to avoid mobile popup blockers, then fetch a
    // short-lived signed token and redirect the tab to the public token URL.
    let popup = null;
    try { popup = window.open("about:blank", "_blank"); } catch (_) { popup = null; }
    try {
      const { data } = await api.get(`/ats/applicants/${aid}/cv-token`);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ats/cv-by-token/${data.token}`;
      if (popup && !popup.closed) { popup.location.href = url; }
      else { window.location.href = url; }
    } catch (e) {
      if (popup && !popup.closed) popup.close();
      alert(e?.response?.data?.detail || "تعذّر فتح ملف السيرة");
    }
  };

  const openPreview = async () => {
    // On mobile, the most reliable approach is to navigate the SAME tab
    // (popups + async fetches are aggressively blocked). On desktop we open
    // a new tab synchronously to keep the dashboard handy.
    const isMobile = (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) ||
                     /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "");
    let popup = null;
    if (!isMobile) {
      try { popup = window.open("about:blank", "_blank"); } catch (_) { popup = null; }
    }
    try {
      const { data } = await api.post("/ats/preview-test");
      const target = data.preview_url;
      if (popup && !popup.closed) {
        popup.location.href = target;
      } else {
        // Mobile (or pop-up blocked) — open in same tab.
        window.location.href = target;
      }
    } catch (e) {
      if (popup) { try { popup.close(); } catch (_) { /* ignore */ } }
      alert(e?.response?.data?.detail || "تعذّر فتح المعاينة");
    }
  };

  const openReport = async (aid) => {
    setDrawerLoading(true);
    setDrawerData(null);
    try {
      const { data } = await api.get(`/ats/applicants/${aid}/full-report`);
      setDrawerData(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "تعذّر تحميل التقرير");
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => setDrawerData(null);

  const decideFromDrawer = async (action) => {
    if (!drawerData?.applicant?.id) return;
    const aid = drawerData.applicant.id;
    const map = {
      approve: "strong",      // Approve for manual contact
      reject:  "rejected",    // Reject politely
      review:  "reserve",     // Keep for review
    };
    const next = map[action];
    if (!next) return;
    await api.patch(`/ats/applicants/${aid}/status`, { status: next });
    closeDrawer();
    load();
  };

  const css = {
    wrap: { minHeight: "calc(100vh - 80px)", background: "#0a0a0a", color: "#fff", padding: "20px 16px", direction: "rtl" },
    container: { maxWidth: 1280, margin: "0 auto" },
    h1: { fontSize: 24, fontWeight: 900, color: "#d4af37", marginBottom: 14 },
    statBar: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 },
    stat: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 12, padding: "12px 14px" },
    statLabel: { fontSize: 11, color: "#a0a0a0", marginBottom: 4 },
    statValue: { fontSize: 26, fontWeight: 900, color: "#d4af37", fontVariantNumeric: "tabular-nums" },
    linkBox: { display: "flex", gap: 8, alignItems: "center", padding: 10, background: "rgba(212,175,55,0.06)", border: "1px dashed rgba(212,175,55,0.4)", borderRadius: 10, marginBottom: 18 },
    linkInput: { flex: 1, background: "transparent", border: "none", color: "#d4af37", fontWeight: 700, outline: "none", fontSize: 13 },
    btnGold: { padding: "8px 14px", background: "#d4af37", color: "#000", border: "none", borderRadius: 8, fontWeight: 800, cursor: "pointer", fontSize: 13 },
    filterRow: { display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" },
    pill: (active) => ({ padding: "6px 12px", borderRadius: 999, border: `1.5px solid ${active ? "#d4af37" : "rgba(212,175,55,0.3)"}`, background: active ? "#d4af37" : "transparent", color: active ? "#000" : "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }),
    search: { flex: 1, minWidth: 200, padding: "8px 12px", background: "rgba(0,0,0,0.5)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, color: "#fff", fontSize: 13, outline: "none" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 },
    card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 14, padding: 16, position: "relative" },
    cardHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
    name: { fontSize: 15, fontWeight: 900, color: "#fff" },
    sub: { fontSize: 11, color: "#a0a0a0" },
    scoreLine: { display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 12, borderBottom: "1px dashed rgba(212,175,55,0.12)" },
    contactRow: { display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" },
    iconBtn: (color) => ({ padding: "6px 10px", borderRadius: 8, border: `1px solid ${color}40`, background: `${color}15`, color, fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }),
    actionRow: { display: "flex", gap: 4, marginTop: 10, flexWrap: "wrap" },
    action: (color) => ({ flex: 1, minWidth: 60, padding: "6px 8px", borderRadius: 6, border: `1px solid ${color}`, background: "transparent", color, fontSize: 10.5, fontWeight: 800, cursor: "pointer" }),
    drawer: { position: "fixed", top: 0, right: 0, height: "100vh", width: "min(720px, 95vw)", background: "#0f0f17", borderInlineStart: "1px solid rgba(212,175,55,0.3)", zIndex: 200, overflowY: "auto", padding: 22, direction: "rtl", boxShadow: "-20px 0 60px rgba(0,0,0,0.6)" },
    backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 199 },
  };

  if (user && user.role && (user.role.code || user.role_code) && !["ats_recruiter", "stas"].includes((user.role.code || user.role_code).toLowerCase())) {
    return <div style={css.wrap}><div style={css.container}>هذا القسم متاح لحساب ATS فقط.</div></div>;
  }

  return (
    <div style={css.wrap} data-testid="ats-dashboard">
      <div style={css.container}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <h1 style={{ ...css.h1, marginBottom: 0 }}>بوّابة التوظيف ATS</h1>
          <button onClick={() => logout && logout()}
            style={{ padding: "6px 14px", background: "transparent", border: "1px solid #dc2626", color: "#dc2626", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
            data-testid="ats-logout">خروج</button>
        </div>

        <div style={css.linkBox} data-testid="ats-public-link">
          <span style={{ color: "#a0a0a0", fontSize: 12 }}>رابط التقديم العام:</span>
          <input style={css.linkInput} value={publicLink} readOnly onFocus={(e) => e.target.select()} data-testid="ats-link-input" />
          <span style={{ padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: linkActive ? "rgba(16,185,129,0.15)" : "rgba(220,38,38,0.15)", color: linkActive ? "#10b981" : "#dc2626", border: `1px solid ${linkActive ? "#10b981" : "#dc2626"}` }} data-testid="link-status-badge">
            {linkActive ? "● مفعّل" : "● معطّل"}
          </span>
          <button style={css.btnGold} onClick={copyLink} disabled={!linkActive} data-testid="ats-copy-link">{copied ? "✓ تم النسخ" : "نسخ"}</button>
          <button
            style={{ ...css.btnGold, background: "transparent", border: "1.5px solid #d4af37", color: "#d4af37" }}
            onClick={copyAdLink}
            disabled={!linkActive}
            title={`رابط مشفّر جديد ومسار مستقل (/r/...) لا يمرّ بأي شاشة تسجيل دخول.\nمناسب للإعلانات والنشر في فيسبوك/لينكدإن/واتساب.\nالرابط الحالي:\n${adLink}`}
            data-testid="ats-copy-ad-link">{copiedAd ? "✓ تم نسخ رابط الإعلان" : "🔗 نسخ رابط الإعلان (مشفّر)"}</button>
          <a style={{ ...css.btnGold, textDecoration: "none", textAlign: "center", opacity: linkActive ? 1 : 0.5, pointerEvents: linkActive ? "auto" : "none" }} href={publicLink} target="_blank" rel="noreferrer" data-testid="ats-open-link">فتح</a>
          <button
            style={{ ...css.btnGold, background: "rgba(13,124,121,0.18)", border: "1.5px solid #0d7c79", color: "#5eead4" }}
            onClick={generateDemoShare}
            disabled={demoBusy}
            title="يولّد رمزاً سرياً جديداً يفتح الديمو مباشرة (بدون الاختبار). الرمز يتغيّر مع كل ضغطة."
            data-testid="ats-demo-share">{demoBusy ? "جارٍ..." : "📋 إرسال الديمو فقط"}</button>
          <button onClick={toggleLink}
                  style={{ padding: "8px 14px", background: linkActive ? "#dc2626" : "#10b981", color: "#fff", border: "none", borderRadius: 8, fontWeight: 800, cursor: "pointer", fontSize: 13 }}
                  data-testid="toggle-link-btn">{linkActive ? "إلغاء التفعيل" : "تفعيل الرابط"}</button>
          <button onClick={openPreview} style={{ padding: "8px 14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontWeight: 800, cursor: "pointer", fontSize: 13 }} data-testid="ats-preview-test">معاينة الاختبار</button>
        </div>

        {stats && (
          <div style={css.statBar}>
            <div style={css.stat}><div style={css.statLabel}>إجمالي المتقدمين</div><div style={css.statValue}>{stats.total}</div></div>
            <div style={css.stat}><div style={css.statLabel}>رفعوا CV</div><div style={css.statValue}>{stats.with_cv}</div></div>
            <div style={css.stat}><div style={css.statLabel}>أكملوا الاختبار</div><div style={css.statValue}>{stats.completed_test ?? 0}</div></div>
            <div style={css.stat}><div style={css.statLabel}>اجتازوا (≥65)</div><div style={{ ...css.statValue, color: "#10b981" }}>{stats.tested}</div></div>
            <div style={css.stat}><div style={css.statLabel}>أقوياء</div><div style={css.statValue}>{stats.by_status?.strong || 0}</div></div>
            <div style={css.stat}><div style={css.statLabel}>احتياط</div><div style={css.statValue}>{stats.by_status?.reserve || 0}</div></div>
            <div style={css.stat}><div style={css.statLabel}>مرفوضون</div><div style={{ ...css.statValue, color: "#dc2626" }}>{stats.by_status?.rejected || 0}</div></div>
          </div>
        )}

        <PopupsManager />

        {demoShare && (
          <DemoShareModal share={demoShare} onClose={() => setDemoShare(null)} onRegen={generateDemoShare} busy={demoBusy} />
        )}

        <div style={css.filterRow}>
          <input style={css.search} placeholder="بحث بالاسم أو الجوال أو الإيميل…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="ats-search" />
          <button style={css.pill(!statusFilter)} onClick={() => setStatusFilter("")} data-testid="filter-all">الكل</button>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <button key={k} style={css.pill(statusFilter === k)} onClick={() => setStatusFilter(k)} data-testid={`filter-${k}`}>{v.ar}</button>
          ))}
        </div>

        {/* Incomplete-attempts toggle — hide CV-only uploads without test */}
        <label style={{
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
          padding: "8px 12px", marginBottom: 10, fontSize: 12.5, fontWeight: 600,
          background: includeIncomplete ? "rgba(220,38,38,0.1)" : "rgba(13,124,121,0.08)",
          border: `1px solid ${includeIncomplete ? "rgba(220,38,38,0.35)" : "rgba(13,124,121,0.35)"}`,
          borderRadius: 8, color: includeIncomplete ? "#fca5a5" : "#5eead4", width: "fit-content",
        }} data-testid="toggle-incomplete">
          <input type="checkbox" checked={includeIncomplete} onChange={(e) => setIncludeIncomplete(e.target.checked)} />
          {includeIncomplete
            ? <>عرض المرشّحين الذين لم يكملوا الاختبار <span style={{ color: "#fca5a5", fontWeight: 800 }}>({incompleteCount})</span></>
            : <>إخفاء من رفع CV بدون اختبار {incompleteCount > 0 && <span style={{ color: "#9a9a9f", fontWeight: 700 }}>({incompleteCount} مخفي)</span>}</>
          }
        </label>

        {/* Bulk-action toolbar */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", background: selected.size > 0 ? "rgba(220,38,38,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${selected.size > 0 ? "rgba(220,38,38,0.35)" : "rgba(212,175,55,0.15)"}`, borderRadius: 10, marginBottom: 12 }} data-testid="bulk-toolbar">
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "#cbd5e1", fontWeight: 700 }}>
            <input type="checkbox" checked={allSelected} onChange={() => allSelected ? clearSelected() : selectAll()} data-testid="select-all-checkbox" />
            {allSelected ? "إلغاء التحديد الكل" : "تحديد الكل"}
          </label>
          <span style={{ fontSize: 12, color: "#9a9a9f" }} data-testid="selected-count">المحدّد: {selected.size}</span>
          <button onClick={clearSelected} disabled={selected.size === 0}
                  style={{ padding: "6px 12px", background: "transparent", border: "1px solid #6b7280", color: "#9a9a9f", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: selected.size === 0 ? "not-allowed" : "pointer", opacity: selected.size === 0 ? 0.5 : 1 }} data-testid="clear-selected">إلغاء التحديد</button>
          <button onClick={deleteSelected} disabled={selected.size === 0}
                  style={{ padding: "6px 12px", background: "#dc2626", border: "none", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: selected.size === 0 ? "not-allowed" : "pointer", opacity: selected.size === 0 ? 0.5 : 1 }} data-testid="bulk-delete-btn">حذف المحدّد ({selected.size})</button>
        </div>

        <div style={css.grid} data-testid="ats-cards">
          {items.length === 0 && <div style={{ gridColumn: "1/-1", textAlign: "center", color: "#a0a0a0", padding: 40 }}>لا يوجد متقدمون حالياً.</div>}
          {items.map((it) => {
            const st = STATUS_LABELS[it.status] || STATUS_LABELS.new;
            const tr = it.test_result || {};
            const cv = it.cv_match || {};
            const wa = (it.whatsapp || it.mobile || "").replace(/[^\d+]/g, "");
            const isPhase3 = tr.version === "phase3";
            const isDemoDirect = it.source === "demo_direct";
            const cardStyle = isDemoDirect
              ? { ...css.card,
                  background: "linear-gradient(180deg, rgba(220,38,38,0.16), rgba(220,38,38,0.05))",
                  border: "2px solid #dc2626",
                  outline: selected.has(it.id) ? "2px solid #fff" : "none",
                  outlineOffset: -2,
                  boxShadow: "0 0 24px rgba(220,38,38,0.25)" }
              : { ...css.card, outline: selected.has(it.id) ? "2px solid #dc2626" : "none", outlineOffset: -2 };
            return (
              <div key={it.id} style={cardStyle} data-testid={`ats-card-${it.id}`}>
                {isDemoDirect && (
                  <div style={{
                    position: "absolute", top: -10, right: 14,
                    background: "#dc2626", color: "#fff",
                    padding: "4px 12px", borderRadius: 999,
                    fontSize: 11, fontWeight: 900, letterSpacing: 0.5,
                    boxShadow: "0 4px 10px rgba(220,38,38,0.5)",
                  }} data-testid={`demo-direct-badge-${it.id}`}>📋 ديمو مباشر</div>
                )}                <div style={css.cardHead}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelected(it.id)}
                           data-testid={`card-select-${it.id}`} style={{ marginTop: 4, cursor: "pointer", width: 16, height: 16 }} />
                    <div>
                      <div style={css.name}>{it.full_name}</div>
                      <div style={css.sub}>{it.country} {it.city ? `• ${it.city}` : ""}</div>
                      <div style={css.sub}>تاريخ التقديم: {(it.created_at || "").slice(0, 10)}</div>
                      {it.referral_source && <div style={{ ...css.sub, color: "#10b981" }} data-testid={`referral-${it.id}`}>
                        المصدر: {{ facebook: "فيسبوك", linkedin: "لينكدإن", whatsapp: "واتساب", other: "أخرى" }[it.referral_source] || it.referral_source}
                      </div>}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                    <div style={{ padding: "4px 10px", borderRadius: 999, background: `${st.color}20`, border: `1px solid ${st.color}`, color: st.color, fontSize: 11, fontWeight: 800 }}>{st.ar}</div>
                    <button onClick={() => deleteOne(it.id, it.full_name)}
                            title="حذف نهائي"
                            style={{ padding: "4px 10px", border: "1px solid #dc2626", background: "transparent", color: "#dc2626", borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: "pointer" }}
                            data-testid={`delete-card-${it.id}`}>🗑 حذف</button>
                  </div>
                </div>

                <div style={css.scoreLine}><span style={{ color: "#a0a0a0" }}>نتيجة CV</span><span style={{ fontWeight: 800, color: cv.score >= 60 ? "#16a34a" : cv.score >= 40 ? "#eab308" : "#dc2626" }}>{cv.score != null ? `${cv.score}/100` : "—"} {cv.recommendation ? `• ${cv.recommendation}` : ""}</span></div>

                {/* Experience badges — quick glance for the recruiter */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, marginBottom: 6 }} data-testid={`exp-row-${it.id}`}>
                  <span style={{
                    padding: "3px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                    background: it.has_visa_experience ? "rgba(16,185,129,0.15)" : "rgba(220,38,38,0.12)",
                    color: it.has_visa_experience ? "#10b981" : "#fca5a5",
                    border: `1px solid ${it.has_visa_experience ? "#10b981" : "#dc2626"}`,
                  }}>
                    {it.has_visa_experience ? `✓ خبرة تأشيرات${(it.years_experience||0) > 0 ? ` · ${it.years_experience}سنة` : ""}` : "✗ بدون خبرة تأشيرات"}
                  </span>
                  {it.english_level && <span style={{
                    padding: "3px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                    background: "rgba(212,175,55,0.12)", color: "#d4af37", border: "1px solid rgba(212,175,55,0.4)",
                  }}>
                    إنجليزي: {{ fluent: "طلق", good: "جيد", basic: "أساسي" }[it.english_level] || it.english_level}
                  </span>}
                </div>
                <div style={css.scoreLine}>
                  <span style={{ color: "#a0a0a0" }}>الاختبار الأولي</span>
                  <span style={{ fontWeight: 800, color: (tr.raw_score || tr.final_score) >= 65 ? "#16a34a" : (tr.raw_score || tr.final_score) >= 40 ? "#eab308" : "#dc2626" }}>
                    {tr.raw_score != null ? `${tr.raw_score}/100` : (tr.final_score != null ? `${tr.final_score}/100` : "لم يُختبر")} {tr.verdict ? `• ${tr.verdict}` : ""}
                  </span>
                </div>
                {isPhase3 && (
                  <>
                    <div style={css.scoreLine}><span style={{ color: "#a0a0a0" }}>الإجابات الصحيحة</span><span>{tr.correct_answers}/{tr.total_questions}</span></div>
                    <div style={css.scoreLine}><span style={{ color: "#a0a0a0" }}>Pop-up أُجيب / فات</span><span>{tr.popup_answered} / {tr.popup_missed}</span></div>
                    <div style={css.scoreLine}><span style={{ color: "#a0a0a0" }}>زمن مستغرق</span><span>{Math.floor((tr.time_spent_seconds || 0) / 60)}m {(tr.time_spent_seconds || 0) % 60}s</span></div>
                  </>
                )}
                {(tr.anti_cheat?.flags_total || tr.anti_cheat_summary?.flags || 0) > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: "#dc2626", fontWeight: 700 }} data-testid={`flag-warning-${it.id}`}>
                    ⚠ {tr.anti_cheat?.flags_total ?? tr.anti_cheat_summary?.flags} علامات سلوكية
                  </div>
                )}

                <div style={css.contactRow}>
                  {it.mobile && <a href={`tel:${it.mobile}`} style={css.iconBtn("#16a34a")} data-testid={`call-${it.id}`}>📞 اتصال</a>}
                  {wa && <a href={`https://wa.me/${wa.replace("+", "")}`} target="_blank" rel="noreferrer" style={css.iconBtn("#25D366")} data-testid={`wa-${it.id}`}>WhatsApp</a>}
                  {it.email && <a href={`mailto:${it.email}`} style={css.iconBtn("#3b82f6")} data-testid={`mail-${it.id}`}>إيميل</a>}
                  {it.cv_filename && it.cv_token && <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/ats/cv-by-token/${it.cv_token}`}
                      target="_blank" rel="noreferrer"
                      style={{ ...css.iconBtn("#d4af37"), textDecoration: "none" }}
                      data-testid={`cv-${it.id}`}>📄 CV</a>}
                  <button onClick={() => openReport(it.id)} style={css.iconBtn("#d4af37")} data-testid={`report-${it.id}`}>📊 التقرير الكامل</button>
                  <a href={`/ats/sheet/${it.id}`} target="_blank" rel="noreferrer" style={css.iconBtn("#0d7c79")} data-testid={`sheet-${it.id}`}>📋 ورقة التقييم الكاملة</a>
                </div>

                <div style={css.actionRow}>
                  <button style={css.action("#16a34a")} onClick={() => setStatus(it.id, "strong")} data-testid={`mark-strong-${it.id}`}>قبول</button>
                  <button style={css.action("#eab308")} onClick={() => setStatus(it.id, "trial")} data-testid={`mark-trial-${it.id}`}>تجريب</button>
                  <button style={css.action("#a855f7")} onClick={() => setStatus(it.id, "reserve")} data-testid={`mark-reserve-${it.id}`}>احتياط</button>
                  <button style={css.action("#dc2626")} onClick={() => setStatus(it.id, "rejected")} data-testid={`mark-reject-${it.id}`}>رفض</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Full Report Drawer */}
      {(drawerData || drawerLoading) && (
        <>
          <div style={css.backdrop} onClick={closeDrawer} data-testid="drawer-backdrop" />
          <div style={css.drawer} data-testid="report-drawer">
            {drawerLoading && <div style={{ color: "#a0a0a0", textAlign: "center", marginTop: 40 }}>جارٍ التحميل…</div>}
            {drawerData && <FullReport data={drawerData} onClose={closeDrawer} onDecide={decideFromDrawer} />}
          </div>
        </>
      )}
    </div>
  );
}

// ──────────── DemoShareModal — generated PIN + share URL ────────────
function DemoShareModal({ share, onClose, onRegen, busy }) {
  const fullUrl = `${window.location.origin}${share.url}`;
  const expDate = share.expires_at ? new Date(share.expires_at).toLocaleString("ar-SA") : "";
  const [copied, setCopied] = useState("");

  const copy = async (txt, label) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch (_) { /* ignore */ }
  };

  const waMsg = `بيئة التقييم العملي\nالرابط: ${fullUrl}\nالرمز السرّي: ${share.pin}\n(صالح 24 ساعة)`;
  const waLink = `https://wa.me/?text=${encodeURIComponent(waMsg)}`;

  return (
    <>
      <div onClick={onClose}
           style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 110 }}
           data-testid="demo-share-backdrop" />
      <div data-testid="demo-share-modal"
           style={{ position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
                    background: "#13131a", color: "#fff", borderRadius: 14, padding: 24,
                    width: "92%", maxWidth: 440, zIndex: 111, direction: "rtl",
                    border: "1px solid rgba(13,124,121,0.5)", boxShadow: "0 20px 80px rgba(0,0,0,0.7)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: "#5eead4" }}>📋 رابط تجربة الديمو</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#9a9a9f", fontSize: 22, cursor: "pointer" }} data-testid="demo-share-close">×</button>
        </div>

        <div style={{ fontSize: 12.5, color: "#9a9a9f", lineHeight: 1.7, marginBottom: 14 }}>
          أرسل الرابط والرمز السري للشخص الذي تريد تجربته. الديمو يحتوي على ٥ سيناريوهات (تركيا/فرنسا/كندا/ألمانيا) بأنواع مختلفة (فردي • عائلة • عائلة مع خادمة • مقيم).
        </div>

        <div style={{ background: "#0a0a0f", border: "1px solid #2a2a35", borderRadius: 10, padding: 14, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "#9a9a9f", fontWeight: 700, marginBottom: 4 }}>الرابط</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input readOnly value={fullUrl}
                   onFocus={(e) => e.target.select()}
                   style={{ flex: 1, padding: 8, background: "transparent", border: "1px solid #2a2a35",
                            borderRadius: 6, color: "#fff", fontSize: 12.5 }} data-testid="demo-share-url" />
            <button onClick={() => copy(fullUrl, "url")}
                    style={{ padding: "8px 12px", background: "#d4af37", border: "none",
                             color: "#0a0a0f", fontWeight: 800, borderRadius: 6, cursor: "pointer", fontSize: 12 }}
                    data-testid="demo-share-copy-url">{copied === "url" ? "✓" : "نسخ"}</button>
          </div>
        </div>

        <div style={{ background: "#0a0a0f", border: "1px solid #2a2a35", borderRadius: 10, padding: 14, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "#9a9a9f", fontWeight: 700, marginBottom: 4 }}>الرمز السري (يتغيّر مع كل ضغطة)</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <div style={{ flex: 1, padding: "12px 16px", background: "transparent", border: "1px solid #2a2a35",
                          borderRadius: 6, color: "#5eead4", fontSize: 26, fontWeight: 900,
                          textAlign: "center", letterSpacing: 8, fontFamily: "monospace" }}
                 data-testid="demo-share-pin">{share.pin}</div>
            <button onClick={() => copy(share.pin, "pin")}
                    style={{ padding: "8px 12px", background: "#d4af37", border: "none",
                             color: "#0a0a0f", fontWeight: 800, borderRadius: 6, cursor: "pointer", fontSize: 12 }}
                    data-testid="demo-share-copy-pin">{copied === "pin" ? "✓" : "نسخ"}</button>
          </div>
        </div>

        <div style={{ fontSize: 11, color: "#9a9a9f", textAlign: "center", marginBottom: 14 }}>
          {expDate && <>صالح حتى: {expDate}</>}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <a href={waLink} target="_blank" rel="noreferrer"
             style={{ flex: 1, padding: "10px 14px", background: "#25D366", color: "#0a0a0f",
                      textAlign: "center", textDecoration: "none", borderRadius: 8,
                      fontWeight: 800, fontSize: 13 }}
             data-testid="demo-share-wa">📱 إرسال عبر WhatsApp</a>
          <button onClick={() => copy(`${fullUrl}\nالرمز: ${share.pin}`, "both")}
                  style={{ flex: 1, padding: "10px 14px", background: "transparent",
                           border: "1px solid #d4af37", color: "#d4af37", borderRadius: 8,
                           fontWeight: 800, fontSize: 13, cursor: "pointer" }}
                  data-testid="demo-share-copy-both">{copied === "both" ? "✓ تم النسخ" : "نسخ الرابط + الرمز"}</button>
        </div>

        <button onClick={onRegen} disabled={busy}
                style={{ width: "100%", marginTop: 10, padding: "9px", background: "transparent",
                         border: "1px dashed #5eead4", color: "#5eead4", borderRadius: 8,
                         fontWeight: 800, fontSize: 12.5, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}
                data-testid="demo-share-regen">
          🔄 توليد رمز جديد
        </button>
      </div>
    </>
  );
}

// ──────────── FullReport — sub-component for drawer body ────────────
function FullReport({ data, onClose, onDecide }) {
  const a = data.applicant || {};
  const r = a.test_result || {};
  const rec = data.recommendation || {};
  const tasks = a.task_reports || [];
  const tierColor = TIER_COLOR[rec.tier] || "#9a9a9f";

  const css = {
    headBtn: { padding: "6px 12px", background: "transparent", border: "1px solid #6b7280", color: "#9a9a9f", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer" },
    section: { marginTop: 20 },
    title: { fontSize: 14, fontWeight: 900, color: "#d4af37", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #2a2a3a" },
    row: { display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 12.5, borderBottom: "1px dashed rgba(212,175,55,0.08)" },
    rowLabel: { color: "#a0a0a0" },
    rowVal: { color: "#fff", fontWeight: 700 },
    taskCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: 10, padding: 12, marginTop: 10 },
    recoBanner: { padding: 14, borderRadius: 12, border: `2px solid ${tierColor}`, background: `${tierColor}15`, marginTop: 8 },
    recoTier: { fontSize: 20, fontWeight: 900, color: tierColor },
    recoScore: { fontSize: 12, color: "#9a9a9f", marginTop: 4 },
    bullet: { fontSize: 12, color: "#cbd5e1", marginTop: 4 },
    btnPrimary: (color) => ({ padding: "10px 14px", background: color, color: "#fff", border: "none", borderRadius: 8, fontWeight: 800, cursor: "pointer", fontSize: 13 }),
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#fff" }} data-testid="drawer-applicant-name">{a.full_name}</div>
          <div style={{ fontSize: 12, color: "#9a9a9f" }}>{a.country} {a.city ? `• ${a.city}` : ""} • {a.email || ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <a href={`/ats/sheet/${a.id}`} target="_blank" rel="noreferrer"
             style={{ ...css.headBtn, borderColor: "#0d7c79", color: "#fff", background: "#0d7c79" }}
             data-testid="drawer-open-sheet">📋 ورقة التقييم الكاملة</a>
          <button onClick={onClose} style={css.headBtn} data-testid="drawer-close">إغلاق</button>
        </div>
      </div>

      {/* ── Auto Recommendation Panel ── */}
      <div style={css.section}>
        <div style={css.title}>التوصية التلقائية</div>
        <div style={css.recoBanner} data-testid="reco-banner">
          <div style={css.recoTier} data-testid="reco-tier">{rec.tier || "—"}</div>
          <div style={css.recoScore}>Composite score: {rec.score}/100</div>
          {(rec.rationale || []).map((line, i) => (
            <div key={i} style={css.bullet} data-testid={`reco-rationale-${i}`}>• {line}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
          <button onClick={() => onDecide("approve")} style={css.btnPrimary("#10b981")} data-testid="reco-approve">Approve for manual contact</button>
          <button onClick={() => onDecide("reject")} style={css.btnPrimary("#dc2626")} data-testid="reco-reject">Reject politely</button>
          <button onClick={() => onDecide("review")} style={css.btnPrimary("#a855f7")} data-testid="reco-review">Keep for review</button>
          <button onClick={onClose} style={css.btnPrimary("#6b7280")} data-testid="reco-close">View full report</button>
        </div>
        <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 8 }}>
          ⚠ التوصية للمساعدة فقط — لا قبول تلقائي. القرار النهائي إداري.
        </div>
      </div>

      {/* ── Initial Screening Section ── */}
      <div style={css.section}>
        <div style={css.title}>الاختبار الأولي</div>
        <div data-testid="initial-screening-block">
          <div style={css.row}><span style={css.rowLabel}>Initial test score</span><span style={css.rowVal}>{r.raw_score ?? r.final_score ?? "—"} / 100</span></div>
          <div style={css.row}><span style={css.rowLabel}>Standard questions score</span><span style={css.rowVal}>{r.standard_score != null ? `${r.standard_score} / 100` : "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Pop-up questions score</span><span style={css.rowVal}>{r.popup_score != null ? `${r.popup_score} / 100` : "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Verdict</span><span style={css.rowVal}>{r.verdict || "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Total initial test time</span><span style={css.rowVal}>{r.time_spent_seconds ? `${Math.floor(r.time_spent_seconds/60)}m ${r.time_spent_seconds%60}s` : "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Average response time</span><span style={css.rowVal}>{r.avg_response_time != null ? `${r.avg_response_time}s` : "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Fast guessing indicators</span><span style={{ ...css.rowVal, color: (r.fast_guess_count || 0) > 2 ? "#fca5a5" : "#10b981" }}>{r.fast_guess_count != null ? `${r.fast_guess_count} answered under 2s` : "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Total questions</span><span style={css.rowVal}>{r.total_questions ?? "—"} <span style={{ color: "#9a9a9f" }}>({r.standard_total ?? 10} std + {r.popup_total ?? 15} pop)</span></span></div>
          <div style={css.row}><span style={css.rowLabel}>Correct answers</span><span style={css.rowVal}>{r.correct_answers ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Wrong answers</span><span style={css.rowVal}>{r.wrong_answers ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Pop-up answered</span><span style={css.rowVal}>{r.popup_answered ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Pop-up missed</span><span style={css.rowVal}>{r.popup_missed ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Paste attempts</span><span style={css.rowVal}>{r.anti_cheat?.paste_attempts ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Tab switches</span><span style={css.rowVal}>{r.anti_cheat?.tab_switches ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Refresh attempts</span><span style={css.rowVal}>{r.anti_cheat?.refresh_attempts ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Warnings</span><span style={css.rowVal}>{r.anti_cheat?.warnings ?? "—"}</span></div>
          <div style={css.row}><span style={css.rowLabel}>Final initial test status</span><span style={{ ...css.rowVal, color: r.passed ? "#10b981" : "#fca5a5" }}>{r.passed === true ? "Passed" : r.passed === false ? "Unable to proceed" : "—"}</span></div>
        </div>

        {/* Per-question time */}
        {(r.per_question || []).length > 0 && (
          <details style={{ marginTop: 10 }}>
            <summary style={{ color: "#d4af37", cursor: "pointer", fontSize: 12, fontWeight: 800 }}>تفاصيل الأسئلة + الوقت</summary>
            <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}>
              {r.per_question.map((q, i) => (
                <div key={i} style={{ ...css.row, color: q.correct ? "#10b981" : "#fca5a5" }}>
                  <span>{q.id} {q.type === "popup_text" ? "(pop-up)" : `(${q.type})`}</span>
                  <span>{q.correct ? "✓" : (q.missed ? "missed" : "✗")} • {q.time_seconds || 0}s</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* ── Per-Task Reports ── */}
      <div style={css.section}>
        <div style={css.title}>تقارير المهام التشغيلية ({tasks.length})</div>
        {tasks.length === 0 && <div style={{ fontSize: 12, color: "#9a9a9f" }}>لم يكمل المرشّح أي مهمة في الـ Sandbox.</div>}
        {tasks.map((t, i) => (
          <div key={i} style={css.taskCard} data-testid={`task-report-${i}`}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ color: "#d4af37", fontWeight: 800 }}>{t.task_id} — {t.scenario}</span>
              <span style={{ color: t.final_task_result === "completed" ? "#10b981" : "#fca5a5", fontWeight: 800, fontSize: 12 }}>
                {t.final_task_result || "—"}
              </span>
            </div>
            <div style={css.row}><span style={css.rowLabel}>Opened</span><span style={css.rowVal}>{(t.opened_at || "").slice(11, 19)}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Completed</span><span style={css.rowVal}>{(t.completed_at || "").slice(11, 19)}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Time spent</span><span style={css.rowVal}>{Math.floor((t.time_spent_seconds || 0) / 60)}m {(t.time_spent_seconds || 0) % 60}s</span></div>
            <div style={css.row}><span style={css.rowLabel}>Data match score</span><span style={{ ...css.rowVal, color: t.data_match_score >= 80 ? "#10b981" : t.data_match_score >= 60 ? "#eab308" : "#fca5a5" }}>{t.data_match_score}/100</span></div>
            <div style={css.row}><span style={css.rowLabel}>Country mismatch</span><span style={css.rowVal}>{t.field_errors?.country_mismatch ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Visa type mismatch</span><span style={css.rowVal}>{t.field_errors?.visa_type_mismatch ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Passport mismatch</span><span style={css.rowVal}>{t.field_errors?.passport_mismatch ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>National ID mismatch</span><span style={css.rowVal}>{t.field_errors?.national_id_mismatch ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Iqama mismatch</span><span style={css.rowVal}>{t.field_errors?.iqama_mismatch ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>BIL handling error</span><span style={css.rowVal}>{t.field_errors?.bil_handling_error ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Biometric city mismatch</span><span style={css.rowVal}>{t.field_errors?.biometric_city_mismatch ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Applicant/Sponsor mismatch</span><span style={css.rowVal}>{t.field_errors?.applicant_sponsor_mismatch ? "✗" : "✓"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Missing-doc handling</span><span style={css.rowVal}>{t.missing_document_handling || "—"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>Appointment handling</span><span style={css.rowVal}>{t.appointment_handling || "—"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>PDF generated</span><span style={css.rowVal}>{t.pdf_generated ? "✓" : "✗"}</span></div>
            <div style={css.row}><span style={css.rowLabel}>PDF uploaded</span><span style={css.rowVal}>{t.pdf_uploaded ? "✓" : "✗"}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}
